import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DetectedNewsPost } from '../../../models/NewsItem';
import { Palette } from 'Palette';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../../../components/styles/StyledButtons';
import StyledModal from '../../../components/styles/StyledModal';
import { StyledTypography } from '../../../components/styles/styles';

type Props = {
  open: boolean;
  sourceUrl: string;
  scanning: boolean;
  detected: DetectedNewsPost[] | null;
  selectedRefs: Set<string>;
  importing: boolean;
  onClose: () => void;
  onScan: () => void;
  onToggleRef: (ref: string) => void;
  onToggleAll: () => void;
  onImport: () => void;
};

export default function NewsAdminImportModal({
  open,
  sourceUrl,
  scanning,
  detected,
  selectedRefs,
  importing,
  onClose,
  onScan,
  onToggleRef,
  onToggleAll,
  onImport,
}: Props) {
  const navigate = useNavigate();
  const allChecked = !!detected && detected.length > 0 && selectedRefs.size === detected.length;
  const someChecked = selectedRefs.size > 0 && !allChecked;

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      title="Import from website"
      actions={
        <>
          <StyledButton
            noTopSpacing
            buttonVariant="inactiveOrCancel"
            sx={SECONDARY_BUTTON_HOVER_SX}
            onClick={onClose}
          >
            Close
          </StyledButton>
          {detected && detected.length > 0 ? (
            <StyledButton
              noTopSpacing
              onClick={onImport}
              disabled={selectedRefs.size === 0 || importing}
              startIcon={importing ? <CircularProgress size={16} /> : undefined}
            >
              Import selected ({selectedRefs.size})
            </StyledButton>
          ) : (
            <StyledButton
              noTopSpacing
              onClick={onScan}
              disabled={scanning}
              startIcon={scanning ? <CircularProgress size={16} /> : undefined}
            >
              Scan
            </StyledButton>
          )}
        </>
      }
    >
      <Box sx={{ mt: 1, minWidth: 420 }}>
        <StyledTypography fontSize="0.875rem" sx={{ mb: 0.5, color: Palette.default.disabledText }}>
          Source URL (read-only)
        </StyledTypography>
        <StyledTypography fontSize="0.875rem" sx={{ wordBreak: 'break-all' }}>
          {sourceUrl || '—'}
        </StyledTypography>
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => navigate('/globalConfig')}
        >
          Change in Global Config
        </Link>

        {detected && detected.length === 0 && (
          <StyledTypography fontSize="0.875rem" sx={{ py: 2 }}>
            No new posts — everything on the site has already been imported.
          </StyledTypography>
        )}

        {detected && detected.length > 0 && (
          <>
            <Box sx={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={onToggleAll}
                      />
                    </TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detected.map((post) => (
                    <TableRow key={post.sourceRef} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRefs.has(post.sourceRef)}
                          onChange={() => onToggleRef(post.sourceRef)}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{post.title}</span>
                          {post.previouslyArchived && (
                            <Chip
                              size="small"
                              label="Archived — re-imports from scratch"
                              sx={{
                                backgroundColor: Palette.warning.background,
                                color: Palette.warning.text,
                                border: `1px solid ${Palette.warning.border}`,
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{post.publishDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    </StyledModal>
  );
}
